import React from 'react';
import { Grid, Group, NumberInput, Select, Text, TextInput } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import type { IEquipoAscensor } from '../../../../modelos/CotizadorNBS/entidades/IEquipoAscensor';
import type { IOpcionesEquiposNBS } from '../../../../modelos/CotizadorNBS';
import type { ErroresFormulario } from '../../utils/camposEspecificacion';
import { conValorActual, maquinasDelProducto } from '../../utils/opcionesEquipos';

interface DatosAscensorVistaProps {
    form: Partial<IEquipoAscensor>;
    /** Cambia uno o varios campos del formulario (p. ej. capacidad y pasajeros juntos). */
    alCambiar: (cambios: Partial<IEquipoAscensor>) => void;
    opciones: IOpcionesEquiposNBS | null;
    errores: ErroresFormulario;
    deshabilitado: boolean;
}

const unidad = (texto: string) => <Text size="xs" c="dimmed" mr="xs" style={{ pointerEvents: 'none' }}>{texto}</Text>;
/** Unidad junto a la flecha del select (la flecha se reemplaza al poner rightSection). */
const unidadSelect = (texto: string) => (
    <Group gap={2} wrap="nowrap" mr="xs" style={{ pointerEvents: 'none' }}>
        <Text size="xs" c="dimmed">{texto}</Text><IconChevronDown size={14} color="var(--mantine-color-gray-6)" />
    </Group>
);
const sinOpciones = 'Sin opciones disponibles';
const columna = { base: 12, xs: 6, md: 4 };

/** Pestaña "Datos del equipo" del ascensor: lo que usa el cálculo. */
export const DatosAscensorVista: React.FC<DatosAscensorVistaProps> = ({ form, alCambiar, opciones, errores, deshabilitado }) => {
    const capacidad = form.Capacidad ? String(form.Capacidad) : undefined;

    return (
        <Grid>
            <Grid.Col span={columna}>
                <TextInput size="xs" label="Nombre" withAsterisk value={form.Nombre || ''} error={errores.Nombre}
                    onChange={(e) => alCambiar({ Nombre: e.currentTarget.value })} disabled={deshabilitado} />
            </Grid.Col>
            <Grid.Col span={columna}>
                <Select size="xs" label="Modelo" withAsterisk searchable nothingFoundMessage={sinOpciones}
                    data={conValorActual(opciones?.ascensores.productos, form.Producto, form.ProductoNombre)}
                    value={form.Producto || null} error={errores.Producto}
                    onChange={(v) => {
                        // La máquina elegida se borra si no es del nuevo modelo.
                        const sigueValida = maquinasDelProducto(opciones, v || '').some(m => m.value === form.Maquina);
                        alCambiar({ Producto: v || '', Maquina: sigueValida ? form.Maquina : '' });
                    }}
                    disabled={deshabilitado} />
            </Grid.Col>
            <Grid.Col span={columna}>
                <Select size="xs" label="Máquina" withAsterisk searchable nothingFoundMessage={sinOpciones}
                    data={conValorActual(maquinasDelProducto(opciones, form.Producto), form.Maquina, form.MaquinaNombre)}
                    value={form.Maquina || null} error={errores.Maquina}
                    onChange={(v) => alCambiar({ Maquina: v || '' })} disabled={deshabilitado} />
            </Grid.Col>
            <Grid.Col span={columna}>
                <Select size="xs" label="Tipo" withAsterisk searchable nothingFoundMessage={sinOpciones}
                    data={conValorActual(opciones?.ascensores.tipos, form.Tipo)}
                    value={form.Tipo || null} error={errores.Tipo}
                    onChange={(v) => alCambiar({ Tipo: v || '' })} disabled={deshabilitado} />
            </Grid.Col>
            <Grid.Col span={columna}>
                <Select size="xs" label="Uso" withAsterisk searchable nothingFoundMessage={sinOpciones}
                    data={conValorActual(opciones?.ascensores.usos, form.Uso)}
                    value={form.Uso || null} error={errores.Uso}
                    onChange={(v) => alCambiar({ Uso: v || '' })} disabled={deshabilitado} />
            </Grid.Col>
            <Grid.Col span={columna}>
                {/* Cada capacidad trae sus pasajeros (catálogo); el back los vuelve a tomar al guardar. */}
                <Select size="xs" label="Capacidad" withAsterisk searchable nothingFoundMessage={sinOpciones}
                    data={conValorActual(opciones?.ascensores.capacidades, capacidad, `${form.Capacidad} kg`)}
                    value={capacidad ?? null} error={errores.Capacidad}
                    onChange={(v) => alCambiar({
                        Capacidad: Number(v) || 0,
                        Pasajeros: opciones?.ascensores.capacidades.find(c => c.value === v)?.pasajeros ?? null
                    })}
                    disabled={deshabilitado} />
            </Grid.Col>
            <Grid.Col span={columna}>
                <Select size="xs" label="Velocidad" withAsterisk searchable nothingFoundMessage={sinOpciones}
                    data={opciones?.ascensores.velocidades || []}
                    value={form.VelocidadIngreso ? String(form.VelocidadIngreso) : null} error={errores.VelocidadIngreso}
                    onChange={(v) => alCambiar({ VelocidadIngreso: Number(v) || 0 })}
                    rightSection={unidadSelect('m/s')} rightSectionWidth={50} disabled={deshabilitado} />
            </Grid.Col>
            <Grid.Col span={columna}>
                <Select size="xs" label="Operador de puerta" withAsterisk searchable nothingFoundMessage={sinOpciones}
                    data={conValorActual(opciones?.ascensores.operadoresPuerta, form.OperadorPuerta, form.OperadorPuertaNombre)}
                    value={form.OperadorPuerta || null} error={errores.OperadorPuerta}
                    onChange={(v) => alCambiar({ OperadorPuerta: v || '' })} disabled={deshabilitado} />
            </Grid.Col>
            <Grid.Col span={columna}>
                <NumberInput size="xs" label="Paradas" withAsterisk min={1} allowNegative={false} allowDecimal={false}
                    value={form.Paradas || ''} error={errores.Paradas}
                    onChange={(v) => alCambiar({ Paradas: Number(v) })} disabled={deshabilitado} />
            </Grid.Col>
            <Grid.Col span={columna}>
                <NumberInput size="xs" label="Potencia Motor" withAsterisk min={0} allowNegative={false}
                    value={form.PotenciaMotor || ''} error={errores.PotenciaMotor}
                    onChange={(v) => alCambiar({ PotenciaMotor: Number(v) })}
                    rightSection={unidad('kW')} rightSectionWidth={35} disabled={deshabilitado} />
            </Grid.Col>
            <Grid.Col span={columna}>
                <NumberInput size="xs" label="Cantidad" withAsterisk min={1} allowNegative={false} allowDecimal={false}
                    value={form.Cantidad || ''} error={errores.Cantidad}
                    onChange={(v) => alCambiar({ Cantidad: Number(v) })} disabled={deshabilitado} />
            </Grid.Col>
            <Grid.Col span={columna}>
                <NumberInput size="xs" label="Costo Equipo" withAsterisk min={0} allowNegative={false} thousandSeparator
                    value={form.CostoFCA ?? ''} error={errores.CostoFCA}
                    onChange={(v) => alCambiar({ CostoFCA: v === '' ? undefined : Number(v) })}
                    leftSection={<Text size="xs" c="dimmed">$</Text>} rightSection={unidad('USD')} rightSectionWidth={40}
                    disabled={deshabilitado} />
            </Grid.Col>
        </Grid>
    );
};
